import { FormEvent, useMemo, useState, useTransition } from 'react'
import { Clock3, FileText, Layers3, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMemorySearch } from '@/hooks/useMemorySearch'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import type { SearchResultItem, SearchStrategy } from '@/types/api'

const STRATEGIES: Array<{ label: string; value: SearchStrategy }> = [
  { label: 'Hybrid', value: 'hybrid' },
  { label: 'Hybrid Lite', value: 'hybrid-lite' },
  { label: 'BM25', value: 'bm25' },
  { label: 'Manual Files', value: 'manual-only' },
]

function formatDateTime(value: string) {
  return new Date(value).toLocaleString()
}

function formatDateLabel(value: string) {
  return new Date(value).toLocaleDateString()
}

function shortId(value: string) {
  if (value.length <= 14) return value
  return `${value.slice(0, 8)}...${value.slice(-4)}`
}

function tokenize(query: string) {
  return query
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
}

function whyShown(result: SearchResultItem, query: string) {
  const tokens = tokenize(query)
  const lowerText = result.text.toLowerCase()
  const matched = tokens.filter((token) => lowerText.includes(token))

  if (matched.length > 0) {
    return `本文に「${matched.join(' / ')}」が含まれ、関連スコア ${result.score.toFixed(3)} で上位表示されました。`
  }

  return `関連スコア ${result.score.toFixed(3)} が高く、${result.sourceType} ソースとして候補に残ったため表示されています。`
}

export function MemoryPage() {
  const resolvedWorkspace = useResolvedWorkspace(import.meta.env.VITE_DEFAULT_WORKSPACE_ID)
  const workspaceId = resolvedWorkspace.workspaceId
  const workspaceName = resolvedWorkspace.workspace?.name ?? workspaceId

  const [draftQuery, setDraftQuery] = useState('')
  const [query, setQuery] = useState('')
  const [strategy, setStrategy] = useState<SearchStrategy>('hybrid')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [isPending, startTransition] = useTransition()
  const requestSourceType = strategy === 'manual-only' ? 'file' : undefined

  const searchQuery = useMemorySearch({
    workspaceId,
    query,
    strategy,
    limit: 20,
    sourceType: requestSourceType,
  })

  const results = searchQuery.data?.results ?? []

  const sourceGroups = useMemo(() => {
    const counts = new Map<string, number>()
    for (const result of results) {
      const key = result.sourceType || 'unknown'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return [...counts.entries()]
      .map(([sourceType, count]) => ({ sourceType, count }))
      .sort((a, b) => b.count - a.count)
  }, [results])

  const filteredResults = useMemo(() => {
    if (sourceFilter === 'all') return results
    return results.filter((result) => result.sourceType === sourceFilter)
  }, [results, sourceFilter])

  const timeline = useMemo(() => {
    const sorted = [...filteredResults].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

    const groups = new Map<string, SearchResultItem[]>()
    for (const item of sorted) {
      const key = item.createdAt.slice(0, 10)
      const current = groups.get(key) ?? []
      current.push(item)
      groups.set(key, current)
    }

    return [...groups.entries()].map(([dateKey, items]) => ({
      dateKey,
      dateLabel: formatDateLabel(items[0]?.createdAt ?? dateKey),
      items,
    }))
  }, [filteredResults])

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const nextQuery = draftQuery.trim()
    startTransition(() => {
      setQuery(nextQuery)
      setSourceFilter('all')
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Memory Explore</h1>
        <p className="text-muted-foreground">
          evidence-first で「何が出て、なぜ出たか」を時系列で確認できます。
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          対象 workspace: <span className="font-medium text-foreground">{workspaceName}</span>
        </p>
      </div>

      {!workspaceId ? (
        <div className="rounded-lg border bg-card p-5 text-sm text-muted-foreground">
          検索対象 workspace が見つかりません。Workspace 設定を確認してください。
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} className="grid gap-3 rounded-lg border bg-card p-4 shadow-sm md:grid-cols-[1fr_auto_auto]">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={draftQuery}
                onChange={(e) => setDraftQuery(e.target.value)}
                placeholder="例: 提出期限 リリース 営業日報"
                className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm"
              />
            </label>
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as SearchStrategy)}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              {STRATEGIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              disabled={isPending}
            >
              検索
            </button>
          </form>

          {query.length === 0 ? (
            <div className="rounded-lg border bg-card p-5 text-sm text-muted-foreground">
              検索バーにキーワードを入力して、Evidence card と時系列表示を確認してください。
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-4 text-sm shadow-sm">
                <span className="font-medium">検索結果: {results.length} 件</span>
                <span className="text-muted-foreground">query: {query}</span>
                <span className="text-muted-foreground">strategy: {strategy}</span>
                {requestSourceType ? (
                  <span className="text-muted-foreground">source: {requestSourceType}</span>
                ) : null}
              </div>

              {sourceGroups.length > 0 && (
                <div className="rounded-lg border bg-card p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <Layers3 className="h-4 w-4 text-muted-foreground" />
                    軽い grouping (source type)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSourceFilter('all')}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs',
                        sourceFilter === 'all'
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:bg-muted'
                      )}
                    >
                      all ({results.length})
                    </button>
                    {sourceGroups.map((group) => (
                      <button
                        type="button"
                        key={group.sourceType}
                        onClick={() => setSourceFilter(group.sourceType)}
                        className={cn(
                          'rounded-full border px-3 py-1 text-xs',
                          sourceFilter === group.sourceType
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border text-muted-foreground hover:bg-muted'
                        )}
                      >
                        {group.sourceType} ({group.count})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery.isLoading || searchQuery.isFetching ? (
                <div className="rounded-lg border bg-card p-5 text-sm text-muted-foreground">
                  検索中...
                </div>
              ) : null}

              {searchQuery.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
                  検索に失敗しました。時間を置いて再試行してください。
                </div>
              ) : null}

              {!searchQuery.isLoading && !searchQuery.isFetching && !searchQuery.isError && timeline.length === 0 ? (
                <div className="rounded-lg border bg-card p-5 text-sm text-muted-foreground">
                  条件に一致する evidence は見つかりませんでした。
                </div>
              ) : null}

              <div className="space-y-4">
                {timeline.map((group) => (
                  <section key={group.dateKey} className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <Clock3 className="h-4 w-4 text-muted-foreground" />
                      {group.dateLabel}
                    </div>
                    <div className="space-y-3">
                      {group.items.map((result) => (
                        <article key={`${result.documentId}-${result.createdAt}-${result.score}`} className="rounded-lg border bg-card p-4 shadow-sm">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="rounded-full bg-muted px-2 py-0.5">{result.sourceType}</span>
                            <span>{formatDateTime(result.createdAt)}</span>
                            <span>score {result.score.toFixed(3)}</span>
                          </div>
                          <p className="mt-3 text-sm leading-6">{result.text}</p>
                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <div className="rounded-md border bg-background p-3 text-xs">
                              <p className="font-semibold text-foreground">Evidence card</p>
                              <p className="mt-1 text-muted-foreground">
                                <FileText className="mr-1 inline h-3 w-3" />
                                docId: {shortId(result.documentId)}
                              </p>
                              <p className="mt-1 text-muted-foreground">source: {result.sourceType}</p>
                            </div>
                            <div className="rounded-md border bg-background p-3 text-xs">
                              <p className="font-semibold text-foreground">why shown</p>
                              <p className="mt-1 text-muted-foreground">{whyShown(result, query)}</p>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
